import api from './api';

/**
 * User Status Checker
 *
 * ONLY responsible for checking user status via APIs.
 * Returns detailed status information about user setup.
 * Does NOT know about flows or requirements.
 */

export interface UserStatus {
  rolesSetup: {
    completed: boolean;
    rolesCount: number;
    requiredCount: number;
  };
  usersInvited: {
    completed: boolean;
    usersCount: number;
    requiredCount: number;
  };
  currentUserId: string;
  currentEmail: string;
}

class UserStatusChecker {
  /**
   * Check current user's setup status by fetching fresh data from APIs
   * Returns: roles count, users count, completion status
   */
  async checkUserStatus(): Promise<UserStatus> {
    try {
      console.log('[UserStatusChecker] Starting user status check...');

      // Step 1: Get current user profile
      const profileResponse = await api.getProfile();
      const currentUser = profileResponse.data;

      if (!currentUser || !currentUser.id) {
        throw new Error('Failed to get user profile');
      }

      console.log('[UserStatusChecker] Current user:', currentUser.id, currentUser.email);

      // Step 2: Get all roles for this tenant (auto-filtered by tenantId from JWT)
      const rolesResponse = await api.getRoles();
      const roles = rolesResponse.data || [];
      const rolesCount = Array.isArray(roles) ? roles.length : 0;

      console.log('[UserStatusChecker] Roles count:', rolesCount);

      // Step 3: Get users in this tenant and count those invited by current user
      const usersResponse = await api.getUsers();
      const tenantUsers = usersResponse.data || [];
      const invitedByMe = Array.isArray(tenantUsers)
        ? tenantUsers.filter(user => user.invitedBy === currentUser.id && user.status !== 'self')
        : [];
      const usersCount = invitedByMe.length;

      console.log('[UserStatusChecker] Users invited by me:', usersCount);

      // Return detailed status
      const status: UserStatus = {
        rolesSetup: {
          completed: rolesCount >= 2, // At least admin + 1 custom role
          rolesCount,
          requiredCount: 2,
        },
        usersInvited: {
          completed: usersCount >= 1, // At least 1 user invited
          usersCount,
          requiredCount: 1,
        },
        currentUserId: currentUser.id,
        currentEmail: currentUser.email,
      };

      console.log('[UserStatusChecker] User status result:', status);
      return status;
    } catch (error) {
      console.error('[UserStatusChecker] Error checking user status:', error);
      throw error;
    }
  }
}

export const userStatusChecker = new UserStatusChecker();
